import { useState } from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import {
  ArrowUturnDownIcon,
  CheckIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import { Card } from '../../../components/base/Card';
import { DataTable } from '../../../components/base/Table';
import { Button } from '../../../components/base/Button';
import { ConfirmDelete } from '../../../components/shared/ConfirmDelete';
import { useClipboardCopy } from '../../../hooks/useClipboardCopy';
import { IApplianceType } from '../../../types/Appliance/ApplianceType';
import useApplianceTypeMutations from '../../../data/appliance/useApplianceTypeMutations';
import { applianceQueryKeys } from '../../../data/QueryKeys';
import { CreateEditApplianceType } from '../../../components/Appliance/CreateEditApplianceType';

const colHelper = createColumnHelper<IApplianceType>();

export function HardwareTypesPage() {
  const { CopyToClipboardButton } = useClipboardCopy();
  const { delApplianceType, updateApplianceType } = useApplianceTypeMutations();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editApplianceType, setEditApplianceType] = useState<
    IApplianceType | undefined
  >(undefined);
  const [deleteApplianceType, setDeleteApplianceType] = useState<
    IApplianceType | undefined
  >(undefined);

  const handleDeleteApplianceType = (applianceType: IApplianceType) => {
    setDeleteApplianceType(applianceType);
    setDeleteOpen(true);
  };

  const handleConfirmDeleteApplianceType = () => {
    delApplianceType.mutate(deleteApplianceType!);
    setDeleteOpen(false);
  };

  const handleRestoreApplianceType = (applianceType: IApplianceType) => {
    updateApplianceType.mutate({ ...applianceType, restore: true });
  };

  const handleEditApplianceType = (applianceType: IApplianceType) => {
    setEditApplianceType(applianceType);
    setEditOpen(true);
  };

  const handleCreateEditClose = () => {
    editApplianceType && setTimeout(() => setEditApplianceType(undefined), 200);
    setEditOpen(false);
  };

  const cols = [
    colHelper.display({
      header: 'Status',
      cell: (info) =>
        info.row.original.disabled ? (
          <XMarkIcon className="w-5 h-5 opacity-70" />
        ) : (
          <CheckIcon className="w-5 h-5 opacity-70" />
        ),
    }),
    colHelper.accessor('name', { header: 'Type' }),
    colHelper.display({
      header: 'Actions',
      cell: (info) => (
        <div className="flex items-center space-x-4">
          {info.cell.row.original.deletedOn ? (
            <ArrowUturnDownIcon
              className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
              onClick={() => handleRestoreApplianceType(info.row.original)}
            />
          ) : (
            <>
              <CopyToClipboardButton iconOnly copyText={info.row.original.id} />
              <PencilIcon
                className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
                onClick={() => handleEditApplianceType(info.row.original)}
              />
              <TrashIcon
                className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
                onClick={() => handleDeleteApplianceType(info.row.original)}
              />
            </>
          )}
        </div>
      ),
    }),
  ];

  return (
    <>
      <CreateEditApplianceType
        open={editOpen}
        onClose={handleCreateEditClose}
        applianceType={editApplianceType}
      />
      <ConfirmDelete
        itemName={deleteApplianceType?.name}
        open={deleteOpen}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={() => handleConfirmDeleteApplianceType()}
      />
      <Card title="Hardware Types">
        <div className="my-6">
          <Button onClick={() => setEditOpen(true)}>Add Type</Button>
        </div>
        <div>
          <DataTable<IApplianceType>
            url="/superadmin/hardware/types"
            queryKey={applianceQueryKeys.superAdminTypes()}
            columns={cols}
            enableShowDeleted
          />
        </div>
      </Card>
    </>
  );
}
